import {
  useSessionStore,
  useGamePlayStore,
  useUserStore,
} from '@/composables/usePiniaStores';
import { useTimerStore } from '@/stores/timerStore';
import { useThemeStore } from '@/stores/themeStore';
import { useGameStore } from '@/stores/gameStore';
import { useModalStore } from '@/stores/modalStore';
import { useUIStore } from '@/stores/uiStore';
import { useTools, type ToolSet } from '@/composables/useTools';
import { useTimeline } from '@/composables/useTimeline';
import type MutatorTools from '@/tools/MutatorTools';
import type MediaTools from '@/tools/MediaTools';
import type MiscTools from '@/tools/MiscTools';
import type RangeTools from '@/tools/RangeTools';
import { useColors } from '@/composables/useColors';
import { getCurrentInstance, ref, Ref } from 'vue';
import { useGameAudioStore } from '@/stores/gameAudioStore';
import {
  usePerformanceMonitor,
  type PerformanceLevel,
} from '@/composables/usePerformanceMonitor';

// Define Color interface
interface Color {
  name: string;
  hex: string;
  rgb: [number, number, number];
}

interface GameSetup {
  tools: ToolSet;
  rangeTools: InstanceType<typeof RangeTools>;
  mutatorTools: InstanceType<typeof MutatorTools>;
  mediaTools: InstanceType<typeof MediaTools>;
  miscTools: InstanceType<typeof MiscTools>;
  setTimeline: ReturnType<typeof useTimeline>['setTimeline'];
  playTimelineAsync: ReturnType<typeof useTimeline>['playTimelineAsync'];
  addEventType: ReturnType<typeof useTimeline>['addEventType'];
  gameAudio: ReturnType<typeof useGameAudioStore>;
  sessionStore: ReturnType<typeof useSessionStore>;
  gamePlayStore: ReturnType<typeof useGamePlayStore>;
  userStore: ReturnType<typeof useUserStore>;
  timerStore: ReturnType<typeof useTimerStore>;
  gameStore: ReturnType<typeof useGameStore>;
  themeStore: ReturnType<typeof useThemeStore>;
  uiStore: ReturnType<typeof useUIStore>;
  modalStore: ReturnType<typeof useModalStore>;
  colors: Ref<Color[]>;
  performanceLevel: ReturnType<typeof ref<PerformanceLevel>>;
  isLowPerformance: ReturnType<typeof ref<boolean>>;
  initializeGameAudio: (gameContext: string) => Promise<void>;
  playGameMusic: (
    keyOrUrl: string,
    options?: { loop?: boolean; volume?: number }
  ) => Promise<void>;
  stopGameAudio: () => void;
}

export function useGameSetup(): GameSetup {
  // Check if we're in a component context
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error(
      'useGameSetup must be called within a component setup function'
    );
  }

  const tools = useTools();
  // Create instances of the tool classes instead of using the class definitions
  const rangeTools = new tools.RangeTools();
  const mutatorTools = new tools.MutatorTools();
  const mediaTools = new tools.MediaTools();
  const miscTools = new tools.MiscTools();

  const { setTimeline, playTimelineAsync, addEventType } = useTimeline();

  // Wrap store access in try/catch to provide better error messages
  let sessionStore: ReturnType<typeof useSessionStore>;
  let gamePlayStore: ReturnType<typeof useGamePlayStore>;
  let gameStore: ReturnType<typeof useGameStore>;
  let userStore: ReturnType<typeof useUserStore>;
  let gameAudio: ReturnType<typeof useGameAudioStore>;
  let timerStore: ReturnType<typeof useTimerStore>;
  let themeStore: ReturnType<typeof useThemeStore>;
  let uiStore: ReturnType<typeof useUIStore>;
  let modalStore: ReturnType<typeof useModalStore>;

  try {
    sessionStore = useSessionStore();
    gamePlayStore = useGamePlayStore();
    gameStore = useGameStore();
    userStore = useUserStore();
    gameAudio = useGameAudioStore();
    timerStore = useTimerStore();
    themeStore = useThemeStore();
    uiStore = useUIStore();
    modalStore = useModalStore();
  } catch (error) {
    if ((error as Error).message.includes('no active Pinia')) {
      throw new Error(
        'Store initialization failed. Make sure you have called app.use(pinia) before mounting your app.'
      );
    }
    throw error;
  }

  const { colors } = useColors();
  const { performanceLevel, isLowPerformance } = usePerformanceMonitor();

  // Connect MutatorTools to gamePlayStore for typedChars updates
  if (mutatorTools.setGamePlayStore) {
    mutatorTools.setGamePlayStore(gamePlayStore);
  }

  // Game-specific audio helper functions
  const initializeGameAudio = async (gameContext: string) => {
    console.log(`🎵 useGameSetup: Initializing audio for game: ${gameContext}`);
    await gameAudio.ensureAudioInitialized();

    if (gameAudio.currentGameContext !== gameContext) {
      await gameAudio.prepareGameTransition(gameContext);
    }
  };

  const playGameMusic = async (
    keyOrUrl: string,
    options: { loop?: boolean; volume?: number } = {}
  ) => {
    console.log(`🎵 useGameSetup: Playing music: ${keyOrUrl}`);
    return gameAudio.playMusicSafe(keyOrUrl, options);
  };

  const stopGameAudio = () => {
    console.log('🎵 useGameSetup: Stopping all game audio');
    gameAudio.stopAllAudio();
  };

  return {
    tools,
    rangeTools,
    mutatorTools,
    mediaTools,
    miscTools,
    setTimeline,
    playTimelineAsync,
    addEventType,
    gameAudio,
    modalStore,
    timerStore,
    sessionStore,
    gamePlayStore,
    gameStore,
    userStore,
    themeStore,
    uiStore,
    colors,
    performanceLevel,
    isLowPerformance,
    initializeGameAudio,
    playGameMusic,
    stopGameAudio,
  };
}
